import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../../../config";
import {
  PERIODS,
  PERIOD_COLUMNS,
  normalizeEstimatingTurnaround,
  computeTotalsRow,
} from "./estimatingTurnaroundHelpers";
import "./EstimatingTurnaround.css";

// "Estimating Turnaround" widget — a full-width, per-salesperson matrix
// comparing estimates Requested vs Completed with % Complete, average turnaround
// (days), and a count of floorplans imported OUTSIDE the CubiCasa API app (so
// the business can spot people skirting that system). Each metric is broken out
// for Week / Quarter / Year to Date, with a Totals row aggregating everyone.
//
// Data comes from GET /Dashboard/api/Dashboard/EstimatingTurnaround (see
// estimatingTurnaroundHelpers for the response contract).

// Render one metric cell, formatting the value with an optional suffix (e.g. %).
// `outsideCubicasa` is a plain count today; it is rendered through this single
// cell so it can become a drill-down link later without touching layout.
const Cell = ({ value, suffix }) => (
  <td className="et-num">
    {value}
    {suffix || ""}
  </td>
);

const Row = ({ row, isTotals }) => (
  <tr className={isTotals ? "et-totals-row" : undefined}>
    <td className={`et-label${isTotals ? " et-totals-label" : ""}`}>
      {row.salespersonName}
    </td>
    {PERIODS.map((p) =>
      PERIOD_COLUMNS.map((c) => (
        <Cell key={`${p.key}-${c.key}`} value={row[p.key][c.key]} suffix={c.suffix} />
      ))
    )}
  </tr>
);

// Unconnected component (exported for testing).
export const EstimatingTurnaround = ({ auth: { auth }, dataScope, userId }) => {
  const [rows, setRows] = useState(null);

  const token = auth && auth[0] ? auth[0].token : null;

  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/EstimatingTurnaround`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        setRows(normalizeEstimatingTurnaround(res.data));
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Estimating Turnaround:", err.message);
        setRows([]);
      });
    return () => {
      active = false;
    };
  }, [token]);

  // For the Sales role, scope the table to the logged-in salesperson's row.
  const visibleRows = useMemo(() => {
    if (!rows) return null;
    if (dataScope === "self" && userId != null) {
      return rows.filter((r) => String(r.salespersonId) === String(userId));
    }
    return rows;
  }, [rows, dataScope, userId]);

  const totals = useMemo(
    () => (visibleRows && visibleRows.length ? computeTotalsRow(visibleRows) : null),
    [visibleRows]
  );

  return (
    <div className="et-widget">
      <h4 className="et-title" style={{ textAlign: "center" }}>
        Estimating Turnaround
      </h4>
      {visibleRows === null ? (
        <p className="et-empty">Loading…</p>
      ) : visibleRows.length === 0 ? (
        <p className="et-empty">No data found.</p>
      ) : (
        <div className="et-scroll">
          <table className="et-table">
            <thead>
              <tr>
                <th rowSpan={2} className="et-corner" />
                {PERIODS.map((p) => (
                  <th key={p.key} colSpan={PERIOD_COLUMNS.length} className="et-group">
                    {p.label}
                  </th>
                ))}
              </tr>
              <tr>
                {PERIODS.map((p) =>
                  PERIOD_COLUMNS.map((c) => (
                    <th key={`${p.key}-${c.key}`} className="et-subhead">
                      {c.label}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <Row key={r.salespersonId ?? r.salespersonName} row={r} />
              ))}
              {totals && <Row row={totals} isTotals />}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(EstimatingTurnaround);
